/**
 * Shared helpers for the booking flow design system.
 *
 * These format durations and time ranges consistently across
 * the BuildPass, CTA dock, and confirmation pass.
 */

export function formatDuration(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Formats a time + duration into a range like "2:30 PM – 5:00 PM".
 *
 * Accepts the real format produced by availabilityCore's generateTimeSlots:
 *   "02:30 PM", "10:00 AM", etc. (en-US toLocaleTimeString with hour/minute)
 *
 * Also accepts 24-hour "HH:MM" as a fallback.
 *
 * Omits the start suffix when both times share the same period:
 *   "2:30 – 5:00 PM" (both PM)
 * Includes both when they differ:
 *   "11:30 AM – 1:00 PM"
 */
export function formatTimeRange(start: string, durationMin: number): string {
  // Parse "02:30 PM" or "14:30" into 24-hour minutes
  const parsed = parseTimeTo24(start);
  if (parsed === null) return start; // unparseable — return as-is

  const endTotalMin = parsed + durationMin;
  const startH24 = Math.floor(parsed / 60);
  const startM = parsed % 60;
  const endH24 = Math.floor(endTotalMin / 60) % 24;
  const endM = endTotalMin % 60;

  const startSuffix = startH24 >= 12 ? 'PM' : 'AM';
  const endSuffix = endH24 >= 12 ? 'PM' : 'AM';

  // Always include minutes ("2:00", not "2") — matches the design's
  // formatTimeRange in screens.jsx
  const fmt12 = (h24: number, m: number): string => {
    let h = h24 % 12;
    if (h === 0) h = 12;
    return `${h}:${String(m).padStart(2, '0')}`;
  };

  const startStr = fmt12(startH24, startM);
  const endStr = fmt12(endH24, endM);

  if (startSuffix === endSuffix) {
    return `${startStr} \u2013 ${endStr} ${endSuffix}`;
  }
  return `${startStr} ${startSuffix} \u2013 ${endStr} ${endSuffix}`;
}

/** Parse "02:30 PM", "2:30 PM", "14:30", or "2:30" into total minutes from midnight. */
function parseTimeTo24(time: string): number | null {
  // Try "HH:MM AM/PM" first
  const ampm = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = parseInt(ampm[2], 10);
    const period = ampm[3].toUpperCase();
    if (period === 'PM' && h < 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h * 60 + m;
  }
  // Try 24-hour "HH:MM"
  const h24 = time.match(/^(\d{1,2}):(\d{2})$/);
  if (h24) {
    return parseInt(h24[1], 10) * 60 + parseInt(h24[2], 10);
  }
  return null;
}
